<script lang="ts">
  import { router, type RouteName } from "./router.svelte";
  import { session } from "$lib/stores/session.svelte";
  import { resolveNavigation } from "$lib/auth/guards";
  import { live } from "$lib/a11y/live.svelte";
  import { holdRelease } from "$lib/release/holds";
  import LiveRegion from "$lib/components/LiveRegion.svelte";
  import Toaster from "$lib/components/Toaster.svelte";
  import UpdateToast from "$lib/components/UpdateToast.svelte";
  import AppLogo from "$lib/components/AppLogo.svelte";

  // Public/auth screens are small and imported eagerly; the heavy authenticated
  // pages are code-split via dynamic import inside AppShell for the production split.
  import Login from "./routes/auth/Login.svelte";
  import SsoContinue from "./routes/auth/SsoContinue.svelte";
  import LoginCode from "./routes/auth/LoginCode.svelte";
  import VerifyEmail from "./routes/auth/VerifyEmail.svelte";
  import SamlCallback from "./routes/auth/SamlCallback.svelte";
  import SamlError from "./routes/auth/SamlError.svelte";
  import SignedOut from "./routes/auth/SignedOut.svelte";
  import ExtensionLogin from "./routes/auth/ExtensionLogin.svelte";
  import InviteAccept from "./routes/onboarding/InviteAccept.svelte";
  import ClaimRedeem from "./routes/onboarding/ClaimRedeem.svelte";
  import UpgradeComplete from "./routes/public/UpgradeComplete.svelte";
  import EmptyAccount from "./routes/onboarding/EmptyAccount.svelte";
  import NotFound from "./routes/NotFound.svelte";
  import AppShell from "./routes/app/AppShell.svelte";
  import AdminShell from "./routes/admin/AdminShell.svelte";
  import NetworkRestricted from "./routes/app/NetworkRestricted.svelte";

  const view = $derived(resolveNavigation(router.match, session));

  const reloadProtectedRoutes = new Set<RouteName>([
    "upgrade.complete",
    "invite.accept",
    "claim",
    "extension.login",
    "org.quote"
  ]);

  $effect(() => {
    const route = router.match.name;
    if (!reloadProtectedRoutes.has(route)) return;
    return holdRelease("protected-route");
  });

  // Redirects are side-effects — never run them in a $derived.
  $effect(() => {
    if (view.kind === "redirect") {
      router.navigate(view.to, { replace: view.replace });
    }
  });

  // Announce page changes to AT and move focus to the heading after navigation.
  $effect(() => {
    const name = router.match.name;
    if (
      view.kind === "public" ||
      view.kind === "admin" ||
      view.kind === "admin-denied" ||
      view.kind === "app" ||
      view.kind === "restricted" ||
      view.kind === "notfound"
    ) {
      queueMicrotask(() => {
        const heading = document.querySelector<HTMLElement>("[data-page-title]");
        heading?.focus();
        if (heading?.textContent) live.announce(heading.textContent);
      });
    }
    void name;
  });

  const publicComponents = {
    login: Login,
    "login.continue": SsoContinue,
    "login.code": LoginCode,
    "verify.email": VerifyEmail,
    "auth.callback": SamlCallback,
    "auth.error": SamlError,
    "signed-out": SignedOut,
    "extension.login": ExtensionLogin,
    "invite.accept": InviteAccept,
    claim: ClaimRedeem,
    "upgrade.complete": UpgradeComplete,
    welcome: EmptyAccount
  } as const;
</script>

<a class="skip-link" href="#main-content">Skip to content</a>
<LiveRegion />

<!--
  Route entrance. `.route-enter` (app.css) is a CSS-only rise on the wrapper
  around whatever view resolved; keying it on `view.kind` replays it whenever the
  product switches surface — splash → auth screen → portal shell → admin console —
  which is exactly when the whole frame changes under the reader.

  The key is `view.kind` and not the route name on purpose: keying per route would
  remount AppShell/AdminShell on every navigation, tearing down the org store's
  entitlement load and refetching it. Motion is not worth a refetch. Within a shell,
  the page swap already has its own entrance (`.stagger` on the stat grid, the
  shell's own `{#key page.key}` remount).

  This does not touch the focus effect above: `[data-page-title]` is still focused on
  the microtask right after navigation, with no delay and no dependency on the
  animation finishing.
-->
{#key view.kind}
  <div class="route-enter">
    {#if view.kind === "loading" || view.kind === "redirect"}
      <div class="splash" role="status" aria-live="polite">
        <AppLogo size={44} />
        <span class="sr-only">Loading…</span>
      </div>
    {:else if view.kind === "public"}
      {#if view.name in publicComponents}
        {@const Component = publicComponents[view.name as keyof typeof publicComponents]}
        <Component />
      {:else}
        <NotFound />
      {/if}
    {:else if view.kind === "admin" || view.kind === "admin-denied"}
      <AdminShell {view} />
    {:else if view.kind === "restricted"}
      <NetworkRestricted organization={view.organization} />
    {:else if view.kind === "app" || view.kind === "denied"}
      <AppShell {view} />
    {:else}
      <NotFound />
    {/if}
  </div>
{/key}

<div class="notification-stack">
  <UpdateToast />
  <Toaster />
</div>

<style>
  .notification-stack {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 150;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    max-width: calc(100vw - 40px);
    pointer-events: none;
  }

  .notification-stack > :global(*) {
    pointer-events: auto;
  }

  .notification-stack > :global(.toaster) {
    position: static;
    max-width: 100%;
  }

  .splash {
    min-height: 100vh;
    display: grid;
    place-items: center;
    animation: pulse 1.6s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .splash {
      animation: none;
    }
  }
  @keyframes pulse {
    50% {
      opacity: 0.55;
    }
  }
</style>
